import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, InputNumber, Select, Tabs, Table, Popconfirm } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import ImageUploader from '../../Ui/ImageUploader';
import { useExcuses } from '../../../hooks/useExcuse';

const { Option } = Select;

const EmployeeFormModal = ({ 
    visible, 
    onCancel, 
    onSubmit, 
    form, 
    editingEmployee 
}) => {
    const salaryType = Form.useWatch('salaryType', form);
    const [activeTab, setActiveTab] = useState('1');
    const [selectedExcuse, setSelectedExcuse] = useState(null);
    const [allowedDays, setAllowedDays] = useState(0);

    const { 
        useGetExcuses, 
        useGetAssignments, 
        useAssignExcuse,
        useRemoveAssignment 
    } = useExcuses();
    
    const { data: allExcuses = [], isLoading: loadingExcuses } = useGetExcuses();
    const { data: existingAssignments = [], isLoading: loadingAssignments, refetch: refetchAssignments } = useGetAssignments(
        editingEmployee?.id
    );
    
    const assignExcuseMutation = useAssignExcuse();
    const removeAssignmentMutation = useRemoveAssignment();

    const handleAddExcuse = async () => {
        if (!selectedExcuse || !editingEmployee?.id) {
            return;
        }

        const excuse = allExcuses.find(e => e.id === selectedExcuse);
        if (!excuse) return;

        try {
            const assignmentData = {
                employeeId: editingEmployee.id,
                excuseId: selectedExcuse,
                allowedDays: allowedDays,
            };

            await assignExcuseMutation.mutateAsync(assignmentData);
            
            // Reset form
            setSelectedExcuse(null);
            setAllowedDays(0);
            
            // Refetch assignments to update the table
            refetchAssignments();
        } catch (error) {
            console.error("Failed to assign excuse:", error);
        }
    };

    const handleRemoveExcuse = async (excuseId) => {
        if (!editingEmployee?.id) return;

        try {
            await removeAssignmentMutation.mutateAsync({
                employeeId: editingEmployee.id,
                excuseId: excuseId,
            });
            
            // Refetch assignments to update the table
            refetchAssignments();
        } catch (error) {
            console.error("Failed to remove excuse assignment:", error);
        }
    };

    const handleModalSubmit = async () => {
        try {
            const values = await form.validateFields();
            await onSubmit(values);
        } catch (err) {
            console.error("Validation failed:", err);
        }
    };

    const excuseColumns = [
        {
            title: 'Excuse Name',
            dataIndex: 'excuseName',
            key: 'excuseName',
            render: (text, record) => {
                const excuse = allExcuses.find(e => e.id === record.excuseId);
                return excuse?.name || text;
            }
        },
        {
            title: 'Allowed Days',
            dataIndex: 'allowedDays',
            key: 'allowedDays',
        },
        {
            title: 'Used Days',
            dataIndex: 'usedDays',
            key: 'usedDays',
            render: (days) => days || 0,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title="Remove this excuse assignment?"
                    onConfirm={() => handleRemoveExcuse(record.excuseId)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button 
                        danger 
                        icon={<DeleteOutlined />} 
                        type="text"
                        size="small"
                        loading={removeAssignmentMutation.isLoading}
                    />
                </Popconfirm>
            ),
        },
    ];

  const basicInfoTab = (
    <Form form={form} layout="vertical">
        {/* Create-only fields */}
        {!editingEmployee && (
            <>
                <div className="flex gap-3">
                    <Form.Item
                        name="firstName"
                        label="First Name"
                        className="flex-1"
                        rules={[{ required: true, message: 'Please enter first name' }]}
                    >
                        <Input placeholder="Enter first name" />
                    </Form.Item>
                    <Form.Item
                        name="lastName"
                        label="Last Name"
                        className="flex-1"
                        rules={[{ required: true, message: 'Please enter last name' }]}
                    >
                        <Input placeholder="Enter last name" />
                    </Form.Item>
                </div>
                <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                        { required: true, message: 'Please enter email' },
                        { type: 'email', message: 'Please enter a valid email' },
                    ]}
                >
                    <Input placeholder="employee@example.com" />
                </Form.Item>
                <Form.Item
                    name="password"
                    label="Password"
                    rules={[{ required: true, message: 'Please enter a password' }]}
                >
                    <Input.Password placeholder="Enter password" />
                </Form.Item>
            </>
        )}

        {/* Edit-only fields */}
        {editingEmployee && (
            <>
                <Form.Item
                    name="fullName"
                    label="Full Name"
                    rules={[{ required: true, message: 'Please enter employee name' }]}
                >
                    <Input placeholder="Enter full name" />
                </Form.Item>
                <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                        { required: true, message: 'Please enter email' },
                        { type: 'email', message: 'Please enter a valid email' },
                    ]}
                >
                    <Input placeholder="employee@example.com" />
                </Form.Item>
                <Form.Item
                    name="newPassword"
                    label="New Password"
                    extra="Leave blank to keep the current password"
                >
                    <Input.Password placeholder="Enter new password (optional)" />
                </Form.Item>
            </>
        )}

        {/* Shared fields */}
        <Form.Item
            name="jobTitle"
            label="Job Title"
            rules={[{ required: true, message: 'Please enter job title' }]}
        >
            <Input placeholder="e.g. Nurse, Receptionist" />
        </Form.Item>

        <Form.Item name="salaryType" label="Salary Type">
            <Select
                placeholder="Select salary type"
                allowClear
                onChange={(value) => {
                    if (!value && value !== 0) {
                        form.setFieldValue('salary', undefined);
                    }
                }}
            >
                <Option value={0}>Monthly Salary</Option>
                <Option value={1}>Hourly Rate</Option>
            </Select>
        </Form.Item>

        <Form.Item
            name="salary"
            label="Salary Amount"
            rules={[
                {
                    required: salaryType !== undefined && salaryType !== null,
                    message: 'Please enter salary amount',
                },
                { type: 'number', min: 200, message: 'Minimum salary is 200' },
            ]}
        >
            <InputNumber
                min={200}
                style={{ width: '100%' }}
                placeholder="200.00"
                disabled={salaryType === undefined || salaryType === null}
            />
        </Form.Item>

        <Form.Item
            name="dailyHours"
            label="Daily Hours"
            rules={[
                { required: true, message: 'Please enter daily hours' },
                { type: 'number', min: 2, message: 'Minimum daily hours is 2' },
            ]}
        >
            <InputNumber min={2} max={24} style={{ width: '100%' }} placeholder="8" />
        </Form.Item>

        <Form.Item
            name="weeklyDays"
            label="Weekly Days"
            rules={[
                { required: true, message: 'Please enter weekly days' },
                { type: 'number', min: 2, message: 'Minimum weekly days is 2' },
            ]}
        >
            <InputNumber min={2} max={7} style={{ width: '100%' }} placeholder="5" />
        </Form.Item>

        <Form.Item name="photoID" label="Employee Photo">
            <ImageUploader form={form} value={form.getFieldValue('photoID')} />
        </Form.Item>
    </Form>
);

    const excusesTab = (
        <div className="space-y-4">
            {!editingEmployee ? (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-yellow-700 text-sm">
                        Please save the employee first before assigning excuses.
                    </p>
                </div>
            ) : (
                <>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Add Excuse</h3>
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <label className="block text-xs text-gray-600 mb-1">Excuse Type</label>
                                <Select
                                    value={selectedExcuse}
                                    onChange={setSelectedExcuse}
                                    placeholder="Select excuse"
                                    style={{ width: '100%' }}
                                    loading={loadingExcuses}
                                >
                                    {allExcuses.map(excuse => (
                                        <Option key={excuse.id} value={excuse.id}>
                                            {excuse.name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="w-32">
                                <label className="block text-xs text-gray-600 mb-1">Allowed Days</label>
                                <InputNumber
                                    value={allowedDays}
                                    onChange={setAllowedDays}
                                    min={0}
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                />
                            </div>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAddExcuse}
                                disabled={!selectedExcuse}
                                loading={assignExcuseMutation.isLoading}
                                className="bg-primary hover:bg-primary_hover"
                            >
                                Add
                            </Button>
                        </div>
                    </div>

                    <Table
                        columns={excuseColumns}
                        dataSource={existingAssignments}
                        loading={loadingAssignments}
                        rowKey="excuseId"
                        pagination={false}
                        locale={{ emptyText: 'No excuses assigned yet' }}
                        size="small"
                    />
                </>
            )}
        </div>
    );

    const tabItems = [
        {
            key: '1',
            label: 'Basic Information',
            children: basicInfoTab,
        },
        {
            key: '2',
            label: `Excuse Assignments ${existingAssignments.length > 0 ? `(${existingAssignments.length})` : ''}`,
            children: excusesTab,
        },
    ];

    return (
        <Modal
            title={
                <span className="text-primary text-lg font-semibold">
                    {editingEmployee ? 'Edit Employee' : 'Add Employee'}
                </span>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="save"
                    type=""
                    onClick={handleModalSubmit}
                    className="bg-primary text-white hover:bg-primary_hover"
                >
                    Save
                </Button>,
            ]}
            destroyOnClose
            width={700}
        >
            <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                items={tabItems}
            />
        </Modal>
    );
};

export default EmployeeFormModal;